<template>
  <PhaseSection
    noColumns
    :hasNextButton="hasNextButton"
    :hasBackButton="hasBackButton"
    @next="$emit('next')"
    @back="$emit('back')"
  >
    <template>
      <TranslationLoading v-if="isTranslating" />
      <template v-else>
        <ReviewHeader
          :validationRequestErrorText="validationRequestErrorText"
          :rev="rev"
          :showEstimated="showEstimated"
          :isImpactValidated="isImpactValidated"
          :isImplemented="isImplemented"
          :hasRequestValidation="hasRequestValidation"
          :showRequestError="showRequestError"
          :loading="isSaving"
          :canImplement="canImplement"
          :canDoFinancialValidation="canDoFinancialValidation"
          :isCompleted="isCompleted"
          @showEstimated="handleShowEstimated"
          @implementRev="$emit('implementRev')"
          @handleRequestValidationClick="handleRequestValidationClick"
          @cancelValidationRequest="$emit('cancelValidationRequest', $event)"
          @validate="$emit('validate')"
          @closeRequestError="showRequestError = false"
        />

        <ReviewValidation
          class="mb-2"
          :isImpactValidated="isImpactValidated"
          :showValidated="!showEstimated"
          :rev="rev"
          :hasRequestValidation="hasRequestValidation"
          :isRevImplemented="isImplemented"
          :editMode="isEditMode"
          @update="updateReviewField"
        />

        <RequestValidationModal
          :show="showRequestModal"
          @hideModal="showRequestModal = false"
          @createValidationRequest="$emit('createValidationRequest', $event)"
        />
        <RequestViewModal
          :show="showRequestViewModal"
          :requestValidation="requestValidationInProgress"
          @hideModal="showRequestViewModal = false"
          @cancelValidationRequest="$emit('cancelValidationRequest', $event)"
        />
      </template>
    </template>
  </PhaseSection>
</template>

<script>
import _ from 'lodash'
import { mapMutations } from 'vuex'

import currencies from '@/locales/currencies.json'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import mixin from '@/mixins/mixin'
import PhaseSection from '@/components/Rev/PhaseSection/PhaseSection.vue'

import _costReductionImage from '@/assets/rev/cost_reduction.svg'
import _costReductionIcon from '@/assets/rev/icon_cost_reduction.svg'
import revenueImage from '@/assets/rev/revenue.svg'
import revenueIcon from '@/assets/rev/icon_revenue.svg'
import investmentImage from '@/assets/rev/investment.svg'
import investmentIcon from '@/assets/rev/icon_investment.svg'
import illustration from '@/assets/rev/review_illustration.svg'
import TranslationLoading from '@/components/Rev/TranslationLoading.vue'
import RequestValidationModal from '@/containers/Modals/RequestValidationModal.vue'
import RequestViewModal from '@/containers/Modals/RequestViewModal.vue'
import ReviewValidation from '@/components/Rev/Review/ReviewValidation.vue'
import ReviewHeader from '@/components/Rev/Review/ReviewHeader.vue'

const FILTERS = {
  ESTIMATED: 'Estimated',
  VALIDATED: 'Validated',
}
export default {
  name: 'ImprovementIdeaReviewStep',

  mixins: [trackerMixin, mixin],

  components: {
    PhaseSection,
    TranslationLoading,
    RequestValidationModal,
    RequestViewModal,
    ReviewValidation,
    ReviewHeader,
  },

  props: {
    isEditMode: {
      type: Boolean,
      required: true,
    },
    rev: {
      type: Object,
      required: true,
    },
    isTranslating: Boolean,
    hasNextButton: Boolean,
    hasBackButton: Boolean,
    hasRequestValidation: Boolean,
    canImplement: Boolean,
    isImplementedValidated: Boolean,
    isSaving: Boolean,
    canDoFinancialValidation: Boolean,
  },

  data() {
    return {
      revCurrency: this.rev?.financialMetadata?.revCurrency,
      organizationCurrency: this.rev.financialMetadata?.organizationCurrency,
      currencySymbol: '',
      organizationCurrencySymbol: '',
      exchangeRate: this.rev.financialMetadata?.exchangeRate,
      illustration,
      costReductionImage: _costReductionImage,
      costReductionIcon: _costReductionIcon,
      revenueImage,
      revenueIcon,
      investmentImage,
      investmentIcon,
      showRequestModal: false,
      showRequestViewModal: false,
      FILTERS,
      selectedFilter: FILTERS.ESTIMATED,
      showEstimated: true,
      showRequestError: false,
      validationRequestErrorText: '',
    }
  },
  mounted() {
    this.selectedFilter = this.showFilters
      ? FILTERS.VALIDATED
      : FILTERS.ESTIMATED
    this.showEstimated = !this.isImpactValidated
  },
  computed: {
    showFilters() {
      return this.isImpactValidated
    },

    validatedFilterIsSelected() {
      return Boolean(this.selectedFilter === FILTERS.VALIDATED)
    },

    validatedIcon() {
      return this.validatedFilterIsSelected
        ? 'mdi-check-decagram'
        : 'mdi-check-decagram-outline'
    },

    isImpactValidated() {
      return Boolean(this.rev?.validation?.financial)
    },

    selectedCategories: {
      get() {
        return this.rev?.categories ?? []
      },

      set(categories) {
        return this.updateCategories(categories)
      },
    },

    costReduction: {
      get() {
        const value = this.validatedFilterIsSelected
          ? this.rev?.validatedReview?.costReduction?.capturedValue
          : this.rev?.review?.costReduction
        const parsedValue = Number(value)

        return _.isNaN(parsedValue) ? null : parsedValue
      },
      set(value) {
        this.$emit('revChanged')
        this.updateCostReduction(value)
      },
    },

    investment: {
      get() {
        const value = this.validatedFilterIsSelected
          ? this.rev?.validatedReview?.investment?.capturedValue
          : this.rev?.review?.investment
        const parsedValue = Number(value)

        return _.isNaN(parsedValue) ? null : parsedValue
      },
      set(value) {
        this.$emit('revChanged')
        this.updateInvestment(value)
      },
    },

    estimationComment: {
      get() {
        if (this.validatedFilterIsSelected)
          return this.rev?.validatedReview?.estimationExplanation
        return this.rev?.review?.estimationComment
      },
      set(value) {
        this.$emit('revChanged')
        this.updateEstimationComment(value)
      },
    },

    revenue: {
      get() {
        const value = this.validatedFilterIsSelected
          ? this.rev?.validatedReview?.revenue?.capturedValue
          : this.rev?.review?.revenue
        const parsedValue = Number(value)

        return _.isNaN(parsedValue) ? null : parsedValue
      },
      set(value) {
        this.$emit('revChanged')
        this.updateRevenue(value)
      },
    },

    categoriesOptions() {
      return this.categoryIds.map(this.buildCategory)
    },

    costReductionExchange() {
      const value = this.validatedFilterIsSelected
        ? this.rev?.validatedReview?.costReduction?.calculatedValue
        : this.rev?.review?.costReductionExchange
      const parsedValue = Number(value)

      return _.isNaN(parsedValue) ? null : parsedValue
    },

    revenueExchange() {
      const value = this.validatedFilterIsSelected
        ? this.rev?.validatedReview?.revenue?.calculatedValue
        : this.rev?.review?.revenueExchange
      const parsedValue = Number(value)

      return _.isNaN(parsedValue) ? null : parsedValue
    },

    investmentExchange() {
      const value = this.validatedFilterIsSelected
        ? this.rev?.validatedReview?.investment?.calculatedValue
        : this.rev?.review?.investmentExchange
      const parsedValue = Number(value)

      return _.isNaN(parsedValue) ? null : parsedValue
    },

    timeInvested() {
      const value = this.validatedFilterIsSelected
        ? this.rev?.validatedReview?.timeTracking?.timeInvested
        : this.rev?.review?.timeInvested
      if (!_.isString(value) && !_.isNumber(value)) return null
      const parsedValue = Number(value)

      return _.isNaN(parsedValue) ? null : parsedValue
    },

    timeReductionByMonth() {
      const value = this.validatedFilterIsSelected
        ? this.rev?.validatedReview?.timeTracking?.timeReductionByMonth
        : this.rev?.review?.timeReductionByMonth
      if (!_.isString(value) && !_.isNumber(value)) return null
      return Number(value)
    },

    siteId() {
      return this.rev.location?.site?._id || this.rev.location?.site
    },

    isImplemented() {
      return (
        this.isImplementedValidated ||
        Boolean(this.rev?.validation?.implementationWasValidated)
      )
    },

    requestValidationInProgress() {
      return this.rev?.validationRequest?.find(
        validationRequest =>
          validationRequest?.status ===
          this.$constants.validationRequestStatus.inProgress
      )
    },

    isCompleted() {
      return this.rev.status === this.$constants.revStatus.completed
    },
  },

  methods: {
    ...mapMutations({
      updateCostReduction: 'improvementIdea/updateCostReduction',
      updateInvestment: 'improvementIdea/updateInvestment',
      updateEstimationComment: 'improvementIdea/updateEstimationComment',
      updateRevenue: 'improvementIdea/updateRevenue',
      updateCategories: 'improvementIdea/updateCategories',
      updateTimeInvested: 'improvementIdea/updateTimeInvested',
      updateTimeReductionByMonth: 'improvementIdea/updateTimeReductionByMonth',
      updateImpactMetrics: 'improvementIdea/updateImpactMetrics',
      updateInvestments: 'improvementIdea/updateInvestments',
      updateStartDate: 'improvementIdea/updateStartDate',
      updateEndDate: 'improvementIdea/updateEndDate',
      setImplementError: 'setImplementError',
    }),

    handleRequestValidationClick() {
      if (!this.isCompleted) {
        this.validationRequestErrorText = this.$t(
          'rev.validation.mark_as_completed_error'
        )
        this.showRequestError = true
        return
      }

      if (!this.isImplemented) {
        this.validationRequestErrorText = this.$t(
          'common.button.mark_this_rev_as_implemented_to_request_validation'
        )
        this.showRequestError = true
        this.setImplementError(true)
        return
      }
      this.trackValidationRequest({
        action: this.hasRequestValidation ? 'view' : 'request',
        type: 'buttonClick',
      })
      this.showRequestError = false
      if (this.hasRequestValidation) {
        this.showRequestViewModal = true
        return
      }

      this.showRequestModal = true
    },

    shouldShowMoneyQuestion(value) {
      return this.isEditMode || _.isNil(value)
    },

    trackEvent(changedField) {
      this.trackUpdate(this.rev, changedField)
    },

    updateReviewField(field, value) {
      this.$emit('revChange')
      switch (field) {
        case 'timeInvested':
          return this.updateTimeInvested(value)
        case 'timeReductionByMonth':
          return this.updateTimeReductionByMonth(value)
        case 'estimationExplanation':
          return this.updateEstimationComment(value)
        case 'categories':
          this.trackUpdate(this.rev, 'changeReviewCategories')
          return this.updateCategories(value)
        case 'impactMetrics':
          return this.updateImpactMetrics(value)
        case 'investments':
          return this.updateInvestments(value)
        case 'startDate':
          return this.updateStartDate(value)
        case 'endDate':
          return this.updateEndDate(value)
      }
    },

    selectFilter(filter) {
      this.trackClickReviewValidationFilter({ type: filter })
      this.selectedFilter = filter
    },

    handleShowEstimated() {
      this.showEstimated = !this.showEstimated
    },
  },

  created() {
    this.currencySymbol = _.get(
      currencies[this.revCurrency],
      'symbol_native',
      ''
    )
    this.organizationCurrencySymbol = _.get(
      currencies[this.organizationCurrency],
      'symbol_native',
      ''
    )
  },
  watch: {
    isCompleted(newVal) {
      if (newVal) {
        this.showRequestError = false
      }
    },
    isImplemented(newVal) {
      if (newVal) {
        this.showRequestError = false
      }
    },
  },
}
</script>
